import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-[#611F69] text-white hover:bg-[#4A154B]',
        outline: 'border border-border bg-white text-text-primary hover:bg-surface-muted',
        ghost: 'text-text-secondary hover:bg-surface-muted hover:text-text-primary',
        // Agent colour variants — used one at a time as accent per agent
        ray: 'bg-ray text-white hover:bg-ray-accent focus-visible:ring-ray',
        olympia: 'bg-olympia text-white hover:bg-olympia-accent focus-visible:ring-olympia',
        yves: 'bg-yves text-white hover:bg-yves-accent focus-visible:ring-yves',
        gail: 'bg-gail text-white hover:bg-gail-accent focus-visible:ring-gail',
        timothy: 'bg-timothy text-white hover:bg-timothy-accent focus-visible:ring-timothy',
        byron: 'bg-byron text-white hover:bg-byron-accent focus-visible:ring-byron',
        peter: 'bg-peter text-white hover:bg-peter-accent focus-visible:ring-peter',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

/**
 * Button component with shadcn/ui-style variants.
 * Supports default, outline, ghost, and per-agent colour variants.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
